import { Box, Typography } from '@mui/material';
import { formatDateByLocale, formatNumber, getBuildingYear, getWithLocale } from '../../utils/utils';
import {
  HomeWorkOutlined,
  FormatListNumberedOutlined,
  BedOutlined,
  WcOutlined,
  WeekendOutlined,
  KitchenOutlined,
  VisibilityOutlined,
  CheckCircleOutline,
  LocalParkingOutlined,
  Key,
  SquareFootOutlined,
  GridViewRounded,
  Straighten,
  AspectRatio,
  AccountBalanceOutlined,
  CleaningServicesOutlined,
  ApartmentOutlined,
  GridViewOutlined,
  HistoryEduOutlined,
  DashboardOutlined,
  BalconyOutlined,
  WarehouseOutlined,
  ColorLensOutlined,
  ExploreOutlined,
  DomainOutlined,
} from '@mui/icons-material';
import { InfoGrid, InfoRow, MainInfoRow } from '../propertydetailSection/PropertyInfo';
import { useTranslation } from 'next-i18next';

const _iconSize = { fontSize: { xs: 24, sm: 32 } };

export const iconMap = (iconSize = _iconSize) => ({
  'land-type': <DashboardOutlined sx={iconSize} />,
  type: <HomeWorkOutlined sx={iconSize} />,
  'floor-unit': <FormatListNumberedOutlined sx={iconSize} />,
  'locateOnFloor': <FormatListNumberedOutlined sx={iconSize} />,
  bedroom: <BedOutlined sx={iconSize} />,
  unit: <DomainOutlined sx={iconSize} />,
  'No. of Unit': <DomainOutlined sx={iconSize} />,
  bathroom: <WcOutlined sx={iconSize} />,
  livingroom: <WeekendOutlined sx={iconSize} />,
  kitchenroom: <KitchenOutlined sx={iconSize} />,
  balcony: <BalconyOutlined sx={iconSize} />,
  View: <VisibilityOutlined sx={iconSize} />,
  Facing: <ExploreOutlined sx={iconSize} />,
  storageroom: <WarehouseOutlined sx={iconSize} />,
  completed: <CheckCircleOutline sx={iconSize} />,
  Parking: <LocalParkingOutlined sx={iconSize} />,
  property_id: <Key sx={iconSize} />,
  'property-type': <HomeWorkOutlined sx={iconSize} />,
  'floor-count': <FormatListNumberedOutlined sx={iconSize} />,
  'building-count': <ApartmentOutlined sx={iconSize} />,
  area: <SquareFootOutlined sx={iconSize} />,
  'land-size': <GridViewRounded sx={iconSize} />,
  Size: <GridViewRounded sx={iconSize} />,
  'square meter': <GridViewRounded sx={iconSize} />,
  roomSize: <GridViewRounded sx={iconSize} />,
  'Land Area': <GridViewRounded sx={iconSize} />,
  'car-unit': <LocalParkingOutlined sx={iconSize} />,
  parking: <LocalParkingOutlined sx={iconSize} />,
  Frontage: <Straighten sx={iconSize} />,
  view: <VisibilityOutlined sx={iconSize} />,
  Direction: <ExploreOutlined sx={iconSize} />,
  Depth: <AspectRatio sx={iconSize} />,
  'property-code': <Key sx={iconSize} />,
  Quota: <AccountBalanceOutlined sx={iconSize} />,
  maidroom: <CleaningServicesOutlined sx={iconSize} />,
  'number-of-floor': <FormatListNumberedOutlined sx={iconSize} />,
  'No. of Building': <ApartmentOutlined sx={iconSize} />,
  'hotel-number-of-unit': <GridViewOutlined sx={iconSize} />,
  'Age of Building': <HistoryEduOutlined sx={iconSize} />,
  project_name: <HomeWorkOutlined sx={iconSize} />,
  pricePerSquareMeter: <GridViewOutlined sx={iconSize} />,
  'Price per unit': <GridViewOutlined sx={iconSize} />,
  landColor: <ColorLensOutlined sx={iconSize} />,
  useable_area: <GridViewOutlined sx={iconSize} />,
  'number-of-building': <ApartmentOutlined sx={iconSize} />,
});

const getLandSize = (data: any, t: any) => {
  const items: JSX.Element[] = [];

  if (data?.Land_Rai) {
    items.push(
      <Box component="span" key="rai" sx={{ mr: 0.5 }}>
        {data.Land_Rai}{' '}
        <Typography component="span" sx={{ color: '#575758' }}>
          {t('rai')}
        </Typography>
      </Box>,
    );
  }

  if (data?.Land_Nga) {
    items.push(
      <Box component="span" key="ngan" sx={{ mr: 0.5 }}>
        {data.Land_Nga}{' '}
        <Typography component="span" sx={{ color: '#575758' }}>
          {t('ngan')}
        </Typography>
      </Box>,
    );
  }

  if (data?.Land_Sqw) {
    items.push(
      <Box component="span" key="sqw" sx={{ mr: 0.5 }}>
        {data.Land_Sqw}{' '}
        <Typography component="span" sx={{ color: '#575758' }}>
          {t('square wa')}
        </Typography>
      </Box>,
    );
  }

  return items.length > 0 ? items : '-';
};

export const getDetailList = (type, data, t, locale) => {
  const getName = (obj) => getWithLocale(obj, 'name', locale) || '-';
  const format = (val) => (Number(val) > 0 ? formatNumber(val) : '-');

  switch (type) {
    case 'Land':
      return [
        { key: 'land-type', value: data?.sub_category_lv1_name },
        {
          key: 'Land Area',
          value: getLandSize(data, t),
        },
        { key: 'Frontage', value: format(data?.Frontage), unit: t('meter') },
        { key: 'Depth', value: format(data?.Depth), unit: t('meter') },
        { key: 'Direction', value: getName(data?.Direction) },
        { key: 'Price per unit', value: format(data?.property_PriceSq), unit: t('bahtPerSquareWa') },
        { key: 'landColor', value: getWithLocale(data, 'land_color_name', locale) },
        { key: 'property_id', value: data?.property_Code },
      ];
    case 'Condominium':
    case 'Condo':
      return [
        { key: 'project_name', value: getWithLocale(data, 'project_Name', locale) },
        { key: 'type', value: data?.sub_category_lv1_name },
        { key: 'roomSize', value: format(data?.property_Size), unit: t('square meter') },
        { key: 'locateOnFloor', value: format(data?.located_on_floor) },
        { key: 'bedroom', value: format(data?.Bedroom || data?.property?.bedroom) },
        { key: 'bathroom', value: format(data?.Bathroom || data?.property?.bathroom) },
        { key: 'pricePerSquareMeter', value: format(data?.property_PriceSq), unit: t('bahtPerSquareMeter') },
        { key: 'livingroom', value: format(data?.Living_Room), unit: t('room') },
        { key: 'parking', value: format(data?.property_Parking), unit: t('car-unit') },
        { key: 'kitchenroom', value: format(data?.Kitchen), unit: t('room') },
        { key: 'storageroom', value: format(data?.Storage_room), unit: t('room') },
        { key: 'balcony', value: format(data?.Balcony), unit: t('room') },
        { key: 'maidroom', value: format(data?.Maidroom), unit: t('room') },
        { key: 'Facing', value: getName(data?.Direction) },
        { key: 'View', value: getName(data?.View) },
        { key: 'Quota', value: data?.property_quota },
        { key: 'Age of Building', value: format(data?.ageOfBuilding) },
        { key: 'completed', value: data.Built ? formatDateByLocale(data.Built, locale.toLowerCase()) : '-' },
        { key: 'property_id', value: data?.property_Code },
      ];
    case 'Commercail':
    case 'Commercial Building':
      return [
        { key: 'project_name', value: getWithLocale(data, 'project_Name', locale) },
        { key: 'type', value: data?.sub_category_lv1_name },
        { key: 'number-of-building', value: format(data?.Buildings) },
        { key: 'number-of-floor', value: format(data?.property_Floor) },
        { key: 'bedroom', value: format(data?.Bedroom) },
        { key: 'bathroom', value: format(data?.Bathroom) },
        {
          key: 'Land Area',
          value: getLandSize(data, t),
        },
        { key: 'useable_area', value: format(data?.property_Size), unit: t('square meter') },
        { key: 'parking', value: format(data?.property_Parking), unit: t('car-unit') },
        { key: 'Age of Building', value: getBuildingYear(Number(data?.built_year)), unit: t('year') },
        { key: 'Frontage', value: format(data?.Frontage), unit: t('meter') },
        { key: 'Depth', value: format(data?.Depth), unit: t('meter') },
        { key: 'property_id', value: data?.property_Code },
      ];
    case 'Detached House':
    case 'House':
    case 'Townhouse-Home':
    case 'Twin house':
    case 'Home Office':
    case 'Luxury House':
      return [
        { key: 'project_name', value: getWithLocale(data, 'project_Name', locale) },
        { key: 'type', value: data?.sub_category_lv1_name },
        { key: 'bedroom', value: format(data?.Bedroom) },
        { key: 'bathroom', value: format(data?.Bathroom) },
        { key: 'livingroom', value: format(data?.Living_Room) },
        { key: 'parking', value: format(data?.property_Parking) },
        {
          key: 'Land Area',
          value: getLandSize(data, t),
        },
        { key: 'useable_area', value: format(data?.property_Size), unit: t('square meter') },
        { key: 'kitchenroom', value: format(data?.Kitchen), unit: t('room') },
        { key: 'maidroom', value: format(data?.Maidroom), unit: t('room') },
        { key: 'storageroom', value: format(data?.Storage_room), unit: t('room') },
        { key: 'balcony', value: format(data?.Balcony) },
        { key: 'Frontage', value: format(data?.Frontage), unit: t('meter') },
        { key: 'Depth', value: format(data?.Depth), unit: t('meter') },
        { key: 'Direction', value: getName(data?.Direction) },
        { key: 'View', value: getName(data?.View) },
        { key: 'property_id', value: data?.property_Code },
      ];
    case 'Warehouse':
    case 'Factory':
      return [
        { key: 'project_name', value: getWithLocale(data, 'project_Name', locale) },
        { key: 'useable_area', value: format(data?.property_Size), unit: t('square meter') },
        { key: 'No. of Building', value: format(data?.Buildings) },
        { key: 'number-of-floor', value: format(data?.property_Floor) },
        { key: 'parking', value: format(data?.property_Parking) },
        { key: 'Age of Building', value: getBuildingYear(Number(data?.built_year)), unit: t('year') },
        {
          key: 'Land Area',
          value: getLandSize(data, t),
        },
        { key: 'Frontage', value: format(data?.Frontage), unit: t('meter') },
        { key: 'Depth', value: format(data?.Depth), unit: t('meter') },
        { key: 'Direction', value: getName(data?.Direction) },
        { key: 'View', value: getName(data?.View) },
        { key: 'property_id', value: data?.property_Code },
      ];
    case 'Apartment':
    case 'Hotel':
    case 'Resort':
      return [
        { key: 'type', value: data?.sub_category_lv1_name },
        { key: 'No. of Unit', value: format(data?.Unit) },
        { key: 'No. of Building', value: format(data?.Buildings) },
        { key: 'number-of-floor', value: format(data?.property_Floor) },
        { key: 'parking', value: format(data?.property_Parking) },
        {
          key: 'Land Area',
          value: getLandSize(data, t),
        },
        { key: 'useable_area', value: format(data?.property_Size), unit: t('square meter') },
        { key: 'Age of Building', value: getBuildingYear(Number(data?.built_year)), unit: t('year') },
        { key: 'Frontage', value: format(data?.Frontage), unit: t('meter') },
        { key: 'Depth', value: format(data?.Depth), unit: t('meter') },
        { key: 'Direction', value: getName(data?.Direction) },
        { key: 'View', value: getName(data?.View) },
        { key: 'property_id', value: data?.property_Code },
      ];
    case 'Office':
    case 'Shop':
      return [
        { key: type === 'Shop' ? 'land-type' : 'type', value: data?.sub_category_lv1_name },
        { key: 'No. of Unit', value: format(data?.Unit) },
        { key: 'No. of Building', value: format(data?.Buildings) },
        { key: 'number-of-floor', value: format(data?.property_Floor) },
        { key: 'parking', value: format(data?.property_Parking) },
        {
          key: 'Land Area',
          value: getLandSize(data, t),
        },
        { key: 'useable_area', value: format(data?.property_Size), unit: t('square meter') },
        { key: 'Frontage', value: format(data?.Frontage), unit: t('meter') },
        { key: 'Depth', value: format(data?.Depth), unit: t('meter') },
        { key: 'Direction', value: getName(data?.Direction) },
        { key: 'View', value: getName(data?.View) },
        { key: 'property_id', value: data?.property_Code },
      ];

    default:
      return [];
  }
};

const RenderDetail = ({ data, locale }) => {
  const { t } = useTranslation();

  const items = getDetailList(data?.type, data, t, locale);
  const SPECIAL_KEYS = ['project_name'];
  const filteredItems = items.filter((item) => SPECIAL_KEYS.includes(item.key));
  const filteredExcludeItems = items.filter((item) => !SPECIAL_KEYS.includes(item.key));
  const topItems = filteredExcludeItems.slice(0, 5);
  const otherItems = filteredExcludeItems.slice(5);

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          my: 4,
        }}
      >
        {topItems.map((item, index) => {
          const icon = iconMap()[item.key];
          const label = t(item.key);
          return (
            <Box
              key={index}
              sx={{
                flex: '1 1 33.3333%',
                maxWidth: '33.3333%',
                '@media (min-width: 800px)': {
                  flex: '1 1 20%',
                  maxWidth: '20%',
                },
              }}
            >
              <MainInfoRow icon={icon} label={label} value={item.value} unit={item.unit} />
            </Box>
          );
        })}
      </Box>

      {filteredItems.map((item, index) => (
        <Box display="flex" alignItems="flex-start" gap={1} sx={{ height: '100%' }} mb={2}>
          {iconMap()[item.key] && (
            <Box
              sx={{
                minWidth: 36,
                mt: 0.3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: (theme) => theme.palette.grey[500],
              }}
            >
              {iconMap()[item.key]}
            </Box>
          )}
          <Box key={index} display="flex" gap={1}>
            <Box display="flex" flexDirection="column" width="100%">
              <Typography
                variant="body1"
                color="#8D8D8D"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.8rem' }, whiteSpace: 'normal' }}
              >
                {t(item.key)}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontSize: { xs: '0.85rem', sm: '0.9rem' },
                  color: 'primary.main',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.value && item.value !== '-' ? item.value : t('not-specified')}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}

      <InfoGrid>
        {otherItems.map((item, index) => {
          const icon = iconMap()[item.key];
          const label = t(item.key);
          return (
            <Box key={index}>
              <InfoRow icon={icon} label={label} value={item.value} unit={item.unit} />
            </Box>
          );
        })}
      </InfoGrid>
    </Box>
  );
};

export default RenderDetail;
